import { cloneDeep, startCase } from 'lodash';
import { Checkout } from '@pdqdev/pdq-shared/dist/interfaces';
import { countryCodeToDisplayNameMap } from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { DisplayCategory } from '@pages/checkout-app/shared/pipes/checkout.pipes';
import { ShippingMethod } from '@pages/checkout-app/types/types';
import { describeCartValueDiscountBasis } from '@pages/checkout-app/configs';
import { DynamicRule, RuleEntity, RuleOperator } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { isEmptyValue, moneyFormatter } from '@app/sharedModule/change-review/change-review.utils';
import {
  ChangeReviewConfig,
  ReviewCurrencies,
  RiskLevel,
  SERVER_MANAGED_FIELDS,
} from '@app/sharedModule/change-review/change-review.model';
import { groupRules, toRuleGroups } from '@app/sharedModule/change-review/rule-groups';
import { describeDynamicRule, WEEKDAYS } from '@app/sharedModule/components/rules-dynamic-form/rule-format';
import {
  LOCATIONS_CONDITION,
  PriceTier,
  SavedCondition,
  ScheduleDay,
  ZoneOption,
} from './shipping-method-review.model';

export const NO_SECTION = 'No section';

const displayCategory = new DisplayCategory();

const SHIP_TO_LABELS: Record<string, string> = {
  all_cities: 'Everywhere',
  'listed-states': 'By states',
  'listed-cities': 'By cities',
  states_from_list: 'By states',
  cities_from_list: 'By cities',
  zones: 'By zones',
  zip_codes: 'By zip codes',
  county: 'By county',
  custom: 'Custom area',
};

const PROMISE_ENGINE_LABELS: Record<string, string> = {
  manual: 'Fixed promise',
  pdqiq: 'MAXX promise',
  linked_calendar: 'Linked calendar',
  linked_multi_ship_date_picker_profile: 'Linked date picker',
  live: 'Live carrier estimate',
};

const HOLIDAY_LABELS: Record<string, string> = {
  none: 'None',
  usa: 'USA holidays',
  jewish: 'Israel holidays',
};

const MONEY_FIELDS = [
  'rate',
  'discountAmount',
  'minOrderPriceRange',
  'maxOrderPriceRange',
  'loyalCustomerPrice',
  'flatAmount',
  'feePerItem.feeRules[].amount',
  'feePerItem.unmatchedFee.amount',
];

// fee features the merchant switches on and off; each seeds defaults into its own subtree
export const TOGGLEABLE_FEE_FEATURES = ['feePerItem', 'feePerCartValue', 'feePerWeekday'];

// Shippings predates the nested fee objects, so both sides carry them untyped.
function featureEnabled(payload: unknown, feature: string): boolean {
  return !!(payload as Record<string, { enabled?: boolean } | undefined>)?.[feature]?.enabled;
}

// Turning a fee feature on, looking around and turning it back off leaves the settings it seeded
// behind: the mode, a default overlap rule. The feature is off either side of that, so none of it
// reaches a shopper, and reporting it asks the merchant to review something they did not do.
// Switching the feature itself still reports, and so does everything inside it once it is on.
export function inertFeeFeatures(before: unknown, after: unknown): string[] {
  return TOGGLEABLE_FEE_FEATURES.filter(
    (feature) => !featureEnabled(before, feature) && !featureEnabled(after, feature),
  );
}

const AREA_LISTS = ['polygonInputValues', 'international.countriesAndRegions', 'international.deliveryAreaZipCodes'];
const SHIP_FROM = ['shippedFromLocation', 'polygonInputValues[].address', 'polygonInputValues[].originZipCode'];
const EXCLUDED_AREAS = ['polygonInputValues[].excludedStates', 'polygonInputValues[].excludedCities'];

const SECTION_FIELDS: Record<string, string[]> = {
  'General settings': [
    'shippingMethodCategory',
    'name',
    'internalName',
    'notes',
    'description',
    'deliveryMethodSectionId',
    'estimatedAverageShippingCost',
  ],
  'On/off and test mode': ['enabled', 'isTestMode'],
  'Shipping rules': ['conditions'],
  'Rates and pricing': [
    'rate',
    'minOrderPriceRange',
    'maxOrderPriceRange',
    'additionalPriceRanges',
    'hasDiscount',
    'discountType',
    'discountAmount',
    'methodCurrency',
    'rateConversion',
    'flatAmount',
    'percentageOfCarrierCost',
    'feePerCartValue',
    'feePerItem',
    'feePerWeekday',
    'calculatePackageDimension',
    'loyalCustomerPrice',
    'loyalCustomerDescription',
    'international.taxPaymentType',
    'international.ddpConfig',
  ],
  'Geo settings': [
    'geoDeliveryArea',
    'deliveryAreaSelectMethod',
    'geoDeliveryAreaMethod',
    'polygonInputValues',
    'international',
    'shippedFromLocation',
    'GEODeliveryAreaZipCodes',
    'GEODeliveryAreaZipCodesOperator',
    'attachLocation',
  ],
  'Delivery service (carrier)': [
    'carrierId',
    'serviceCodeInput',
    'SLABDays',
    'latestArrivalDateToFilter',
    'displayMethodAndServiceLevelName',
    'shipmentExtras',
  ],
  'Delivery promise': [
    'estimatedTimeConversions',
    'deliveryPromiseDeliveryTimeSettings',
    'deliveryPromisePdqIqBaseShippingMethod',
    'deliveryPromisePdqIqPercentile',
    'maxxAiConfiguration',
    'deliveryPromiseDontCalculateOn',
    'deliveryPromiseRestriction',
    'linkedDatePickerExtensionId',
    'linkedMultiShipDatePickerProfile',
    'deliveryPromiseArrivalDateRangeDisplayText',
    'deliveryPromiseArrivalSingleDateDisplayText',
    'displayEstDelivery',
    'displayEstimatedDeliveryBusinessDays',
  ],
  'Active days and times': [
    'deliveryDaysAllWeek',
    'deliveryDaysAndTimes',
    'ordersPerDay',
    'limitUnfulfilledOrders',
    'deliveryDaysAndTimesHolidays',
  ],
  'Blackout dates': ['customBlackoutDates'],
};
const SECTIONS: Record<string, string> = Object.fromEntries(
  Object.entries(SECTION_FIELDS).flatMap(([section, fields]) => fields.map((field) => [field, section])),
);

const LABELS: Record<string, string> = {
  name: 'Name',
  internalName: 'Internal name',
  notes: 'Note',
  enabled: 'Status',
  isTestMode: 'Test mode',
  rate: 'Rate',
  minOrderPriceRange: 'Minimum price',
  maxOrderPriceRange: 'Maximum price',
  additionalPriceRanges: '',
  'additionalPriceRanges[]': 'Additional tier',
  conditions: '',
  geoDeliveryArea: 'Ship to',
  deliveryAreaSelectMethod: 'Ship to',
  geoDeliveryAreaMethod: 'Area method',
  polygonInputValues: '',
  'polygonInputValues[]': '',
  'polygonInputValues[].zones': 'Zone',
  'polygonInputValues[].listedStates': 'State',
  'polygonInputValues[].listedCities': 'City',
  'polygonInputValues[].excludedStates': 'Excluded state',
  'polygonInputValues[].excludedCities': 'Excluded city',
  'polygonInputValues[].originZipCode': 'Origin zip code',
  'polygonInputValues[].address': 'Origin address',
  'polygonInputValues[].listedStatesControl': 'States',
  'polygonInputValues[].zipCodes': 'Zip codes',
  international: '',
  'international.countriesAndRegions': 'Location',
  'international.taxPaymentType': 'Duties and taxes',
  methodCurrency: 'Currency',
  carrierId: 'Carrier',
  SLABDays: 'Max transit time',
  latestArrivalDateToFilter: 'Latest arrival date',
  shippedFromLocation: 'Ships from',
  shippingMethodCategory: 'Delivery method',
  shipmentExtras: '',
  'shipmentExtras.activeServiceProviders': 'Carrier',
  'shipmentExtras.activeProviderServiceLevelsIds': 'Carrier service',
  deliveryMethodSectionId: 'Section',
  estimatedTimeConversions: 'Promise engine',
  estimatedAverageShippingCost: 'Average shipping cost',
  loyalCustomerPrice: 'Loyal customer rate',
  loyalCustomerDescription: 'Loyal customer note',
  ordersPerDay: 'Daily order limit',
  limitUnfulfilledOrders: 'Unfulfilled orders limit',
  customBlackoutDates: '',
  'customBlackoutDates[]': 'Blackout range',
  deliveryPromiseDeliveryTimeSettings: '',
  maxxAiConfiguration: '',
  'maxxAiConfiguration.maxxAiDeliveryPromiseEnabled': 'MAXX AI faster promise',
  'maxxAiConfiguration.maxxAiDeliveryPromiseMinCap': 'MAXX AI minimum',
  'polygonInputValues[].zipCodesOperator': 'Zip codes rule',
  attachLocation: 'Deduct orders from inventory',
  calculatePackageDimension: 'Price by package size',
  feePerItem: 'Per-item fee',
  'feePerItem.enabled': 'On',
  'feePerItem.feeRules': '',
  'feePerItem.feeRules[]': 'Rule',
  'feePerItem.byProductGroup': 'By product group',
  'feePerItem.groupRules': '',
  'feePerItem.groupRules[]': 'Group rule',
  'feePerItem.overlapMode': 'Overlapping rules',
  'feePerItem.unmatchedFee': 'Items in no group',
  'feePerItem.unmatchedFee.amount': 'Fee',
  'feePerItem.unmatchedFee.type': 'Fee type',
  'feePerItem.configurationId': 'Saved setup',
  feePerCartValue: 'Cart value fee',
  'feePerCartValue.enabled': 'On',
  'feePerCartValue.amount': 'Percent',
  feePerWeekday: 'Delivery date fee',
  'feePerWeekday.enabled': 'On',
  'feePerWeekday.feePerWeekdayRules': '',
  'feePerWeekday.feePerWeekdayRules[]': 'Rule',
  displayEstDelivery: 'Show delivery promise',
  displayEstimatedDeliveryBusinessDays: 'Show as business days',
  deliveryPromiseArrivalSingleDateDisplayText: 'Single date text',
  deliveryPromiseArrivalDateRangeDisplayText: 'Date range text',
  deliveryPromisePdqIqPercentile: 'MAXX promise percentile',
  deliveryPromisePdqIqBaseShippingMethod: 'MAXX promise base method',
  deliveryPromiseDontCalculateOn: '',
  'deliveryPromiseDontCalculateOn[]': 'Excluded non-business day',
  deliveryPromiseRestriction: 'Hide promise',
  'deliveryPromiseRestriction.enabled': 'On',
  'deliveryPromiseRestriction.rules': '',
  'deliveryPromiseRestriction.rules[]': 'Rule',
  deliveryDaysAllWeek: 'Always active (24/7)',
  deliveryDaysAndTimes: '',
  deliveryDaysAndTimesHolidays: 'Turn off on holidays',
};

const CRITICAL = [
  'shippingMethodCategory',
  'enabled',
  'isTestMode',
  'conditions',
  'rate',
  'hasDiscount',
  'discountType',
  'discountAmount',
  'minOrderPriceRange',
  'maxOrderPriceRange',
  'additionalPriceRanges',
  // the Ship to radios are stripped from the payload; deliveryAreaSelectMethod carries it
  'geoDeliveryArea',
  'geoDeliveryAreaMethod',
  'deliveryAreaSelectMethod',
  ...AREA_LISTS,
  'GEODeliveryAreaZipCodes',
  'GEODeliveryAreaZipCodesOperator',
  'methodCurrency',
  'freeToggle',
  'freeAmount',
  'estimatedTimeConversions',
  'rateConversion',
  'flatAmount',
  'percentageOfCarrierCost',
  'international.taxPaymentType',
  'international.ddpConfig',
  'carrierId',
  'serviceCodeInput',
  'SLABDays',
  'latestArrivalDateToFilter',
];

const MEDIUM = [
  // which carriers and services live rates come from
  'shipmentExtras.activeServiceProviders',
  'shipmentExtras.activeProviderServiceLevelsIds',
  ...EXCLUDED_AREAS,
  ...SHIP_FROM,
  'attachLocation',
  'name',
  'feePerCartValue',
  'feePerItem',
  'feePerItemEnabled',
  'feePerItemRules',
  'feePerWeekday',
  'calculatePackageDimension',
  'loyalCustomerPrice',
  'deliveryDaysAllWeek',
  'deliveryDaysAndTimes',
  'customBlackoutDates',
  'deliveryPromiseDeliveryTimeSettings',
  'deliveryPromisePdqIqBaseShippingMethod',
  'deliveryPromisePdqIqPercentile',
  'maxxAiConfiguration',
  'deliveryPromiseDontCalculateOn',
  'deliveryPromiseRestriction',
  'ordersPerDay',
  'limitUnfulfilledOrders',
  'deliveryDaysAndTimesHolidays',
  'linkedDatePickerExtensionId',
  'linkedMultiShipDatePickerProfile',
];

const LEVELS: Record<string, RiskLevel> = {
  ...Object.fromEntries(CRITICAL.map((pattern) => [pattern, 'critical'])),
  ...Object.fromEntries(MEDIUM.map((pattern) => [pattern, 'medium'])),
};

export function buildShippingMethodReviewConfig(
  methodName: string,
  currencies: ReviewCurrencies,
  sectionNames: Record<string, string> = {},
  operatorLabel: (operator: string) => string | undefined = () => undefined,
  locationLabel: (locationId: unknown) => string | undefined = () => undefined,
  setupLabel: (configurationId: unknown) => string | undefined = () => undefined,
): ChangeReviewConfig {
  const money = moneyFormatter(currencies);
  const locationNames = (locationIds: unknown) =>
    [locationIds]
      .flat()
      .map((locationId) => locationLabel(locationId) ?? String(locationId))
      .join(', ');

  return {
    subtitle: methodName,
    labels: LABELS,
    sections: SECTIONS,
    ignore: [
      'deliveryPromiseType',
      'shippingMethodGroupId',
      'polygonInputValues[].zonesList',
      // the Ship to choice already says it
      'polygonInputValues[].type',
      // not editable in the UI
      'allConditions',
      // links to server records; the type switch clears them but the server keeps its own
      'shipmentExtrasId',
      'shippingMethodId',
      ...SERVER_MANAGED_FIELDS,
    ],
    normalize: (payload, side) => {
      const sideMoney = (value: unknown) => money(value, side);
      const readable = withCountryNames(
        withReadableSchedule(withSectionName(withSelectedZones(payload), sectionNames)),
      );
      return withPriceTiers(withRuleGroups(readable, operatorLabel, sideMoney), sideMoney);
    },
    format: {
      ...Object.fromEntries(MONEY_FIELDS.map((field) => [field, money])),
      shippedFromLocation: locationNames,
      // An id means nothing to a merchant. A setup with no name is a config private to this method.
      'feePerItem.configurationId': (configurationId) => setupLabel(configurationId) ?? 'This method only',
      shippingMethodCategory: (category) =>
        displayCategory.transform(category as ShippingMethod) ?? startCase(String(category)),
      'shipmentExtras.activeServiceProviders[]': (provider) => startCase(String(provider)),
      deliveryAreaSelectMethod: (method) => SHIP_TO_LABELS[method as string] ?? startCase(String(method)),
      estimatedTimeConversions: (engine) => PROMISE_ENGINE_LABELS[engine as string] ?? startCase(String(engine)),
      'polygonInputValues[].zipCodesOperator': (operator) => startCase(String(operator)),
      'customBlackoutDates[]': (range) => describeBlackoutRange(range as BlackoutRange),
      'feePerWeekday.feePerWeekdayRules[]': (rule, side) =>
        describeWeekdayFee(rule as WeekdayFee, (value) => money(value, side)),
      'feePerItem.groupRules[]': (rule, side) => describeGroupRule(rule as GroupRule, (value) => money(value, side)),
      'feePerItem.overlapMode': (mode) => OVERLAP_LABELS[mode as string] ?? startCase(String(mode)),
      'feePerItem.unmatchedFee.type': (type) => GROUP_FEE_TYPE_LABELS[type as string] ?? startCase(String(type)),
      'deliveryPromiseDontCalculateOn[]': (day) => describeExcludedDay(day as ExcludedDay),
      'deliveryPromiseRestriction.rules[]': (rule) =>
        describeDynamicRule(rule as DynamicRule, {
          paramLabel: (param, value) => (param === 'locationIdsValue' ? locationNames(value) : undefined),
        }),
    },
    levels: LEVELS,
  };
}

// payload paths the server stores under another name
export const SHIPPING_ENTITY_PATHS: Record<string, string> = {
  'deliveryPromiseDeliveryTimeSettings.useMetafieldProcessingTime': 'shippingMethod.useMetafieldProcessingTime',
  'deliveryPromiseDeliveryTimeSettings.minProcessingDays': 'shippingMethod.minProcessingDays',
  'deliveryPromiseDeliveryTimeSettings.maxProcessingDays': 'shippingMethod.maxProcessingDays',
  'deliveryPromiseDeliveryTimeSettings.minTransitDays': 'shippingMethod.minDays',
  'deliveryPromiseDeliveryTimeSettings.maxTransitDays': 'shippingMethod.maxDays',
  'deliveryPromiseDeliveryTimeSettings.minHours': 'shippingMethod.minHours',
  'deliveryPromiseDeliveryTimeSettings.maxHours': 'shippingMethod.maxHours',
  'deliveryPromiseDeliveryTimeSettings.metafieldProcessingTime': 'shippingMethod.metafieldProcessingTime',
  'deliveryPromiseDeliveryTimeSettings.metafieldProcessingTimeFallback':
    'shippingMethod.metafieldProcessingTimeFallback',
  'deliveryPromiseDeliveryTimeSettings.combineConsecutiveDays': 'shippingMethod.combineConsecutiveDays',
};

function withSelectedZones(payload: unknown): unknown {
  const copy = cloneDeep(payload) as { polygonInputValues?: { zonesList?: ZoneOption[]; zones?: string[] }[] };
  copy?.polygonInputValues?.forEach?.((area) => {
    if (area?.zonesList) {
      area.zones = area.zonesList.filter((zone) => zone.value).map((zone) => zone.label);
    }
  });
  return copy;
}

// '' means "No section"
function withSectionName(payload: unknown, sectionNames: Record<string, string>): unknown {
  const copy = payload as { deliveryMethodSectionId?: string };
  if (copy && 'deliveryMethodSectionId' in copy) {
    const id = copy.deliveryMethodSectionId ?? '';
    copy.deliveryMethodSectionId = sectionNames[id] ?? (id || NO_SECTION);
  }
  return copy;
}

export function describePriceTier(tier: PriceTier, money: (value: unknown) => string): string {
  const to = isEmptyValue(tier.maxOrderPriceRange) ? 'No limit' : money(tier.maxOrderPriceRange);
  const range = `${money(tier.minOrderPriceRange ?? 0)} - ${to}: ${money(tier.rate ?? 0)}`;
  if (!tier.hasDiscount) return range;
  const discount =
    tier.discountType === 'percentage' ? `${Number(tier.discountAmount ?? 0)}%` : money(tier.discountAmount ?? 0);
  return `${range} (${discount} discount)`;
}

// the Locations pseudo-rule isn't a rule; a lone group has no subsetGroupId
const isRule = (condition: SavedCondition) => condition.name !== LOCATIONS_CONDITION.name;
const ruleGroupOf = (condition: SavedCondition) => condition.subsetGroupId ?? 1;

export function shippingRuleGroups(conditions: SavedCondition[] = []): SavedCondition[][] {
  return groupRules(conditions.filter(isRule), ruleGroupOf);
}

// a country without regions means the whole country, which the diff would otherwise read as empty
function withCountryNames(payload: unknown): unknown {
  const international = (payload as { international?: { countriesAndRegions?: unknown } })?.international;
  const areas = international?.countriesAndRegions;
  if (areas && typeof areas === 'object' && !Array.isArray(areas)) {
    international.countriesAndRegions = Object.entries(areas as Record<string, string[]>).map(([code, regions]) => {
      const country = countryCodeToDisplayNameMap[code] ?? code;
      return regions?.length ? `${country} (${regions.join(', ')})` : country;
    });
  }
  return payload;
}

// whole tiers, so deleting one shows that tier as removed instead of shifting the rest
function withPriceTiers(payload: unknown, money: (value: unknown) => string): unknown {
  const copy = payload as { additionalPriceRanges?: unknown };
  if (Array.isArray(copy?.additionalPriceRanges)) {
    copy.additionalPriceRanges = (copy.additionalPriceRanges as PriceTier[]).map((tier) =>
      describePriceTier(tier, money),
    );
  }
  return copy;
}

function withRuleGroups(
  payload: unknown,
  operatorLabel: (operator: string) => string | undefined,
  money: (value: unknown) => string,
): unknown {
  const copy = payload as { conditions?: unknown };
  if (Array.isArray(copy?.conditions)) {
    copy.conditions = toRuleGroups((copy.conditions as SavedCondition[]).filter(isRule), ruleGroupOf, (condition) =>
      describeRule(condition, operatorLabel, money),
    );
  }
  return copy;
}

export function describeRule(
  condition: SavedCondition,
  operatorLabel: (operator: string) => string | undefined,
  money: (value: unknown) => string = String,
): string {
  const describeOperator = (operator: string) => operatorLabel(operator) ?? startCase(operator).toLowerCase();
  const name = condition.displayName ?? startCase(condition.name ?? '');
  const operator = condition.condition ? describeOperator(condition.condition) : '';
  return [name, operator, describeRuleValue(condition, describeOperator, money)].filter(Boolean).join(' ');
}

// value2 and optionalValue mean something different per rule type, matching the rule editor
function describeRuleValue(
  condition: SavedCondition,
  describeOperator: (operator: string) => string,
  money: (value: unknown) => string,
): string {
  const [value, secondValue] = (condition.values ?? '').split('||').filter((part) => part && part !== 'null');
  const extra = condition.optionalValue;
  switch (condition.name) {
    case 'shopify_tags':
      return secondValue ? `${value}, quantity ${describeOperator(secondValue)} ${extra ?? ''}`.trim() : value;
    case 'sku':
      return secondValue ? `${value}, quantity is ${secondValue}` : value;
    case 'customerOrderCount':
      return secondValue === 'last_days' ? `${value} within the last ${extra} calendar days` : `${value} (all time)`;
    case 'cartValue':
      return `${money(value)}${describeCartValueDiscountBasis(extra)}`;
    case 'customerSpend':
      return money(value);
    case 'coupons':
      if (secondValue === 'percent') return `${value}%`;
      if (secondValue === 'currency') return money(value);
      return value;
    case 'grossMargin':
      return extra === 'percentage' ? `${value}%` : money(value);
    case 'weight':
      return [value, condition.weightUnit].filter(Boolean).join(' ');
    default:
      return [value, secondValue].filter(Boolean).join(', ');
  }
}

interface BlackoutRange {
  startDate?: string | Date;
  endDate?: string | Date;
  note?: string;
}

interface WeekdayFee {
  weekday?: number | string;
  amount?: number;
  numOfDays?: number;
  customDate?: { start?: string | Date; end?: string | Date } | null;
  nationalHolidays?: { country?: string | null } | null;
  shippingName?: string | null;
  shippingNotes?: string | null;
}

interface ExcludedDay {
  type?: string;
  value?: unknown;
}

function withReadableSchedule(payload: unknown): unknown {
  const copy = payload as {
    deliveryDaysAndTimes?: ScheduleDay[];
    deliveryDaysAndTimesHolidays?: { name?: string }[] | string;
  };
  if (Array.isArray(copy?.deliveryDaysAndTimes)) {
    copy.deliveryDaysAndTimes = Object.fromEntries(
      copy.deliveryDaysAndTimes.map((day, index) => [
        day.day ?? WEEKDAYS[index],
        day.enable ? `${day.fromTime ?? ''}–${day.toTime ?? ''}` : 'Off',
      ]),
    ) as unknown as ScheduleDay[];
  }
  if (Array.isArray(copy?.deliveryDaysAndTimesHolidays)) {
    const name = copy.deliveryDaysAndTimesHolidays[0]?.name ?? 'none';
    copy.deliveryDaysAndTimesHolidays = HOLIDAY_LABELS[name] ?? startCase(name);
  }
  return copy;
}

const formatDate = (date: string | Date | undefined): string =>
  date ? new Date(date).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '';

function describeBlackoutRange(range: BlackoutRange): string {
  const dates = [formatDate(range.startDate), formatDate(range.endDate)].filter(Boolean).join(' – ');
  return range.note ? `${dates} (${range.note})` : dates;
}

const OVERLAP_LABELS: Record<string, string> = {
  [Checkout.RuleOverlapMode.FIRST]: 'Charge the first matching rule',
  [Checkout.RuleOverlapMode.ALL]: 'Charge every matching rule',
};

const GROUP_FEE_TYPE_LABELS: Record<string, string> = {
  [Checkout.GroupFeeType.FIXED]: 'Fixed amount',
  [Checkout.GroupFeeType.PERCENT_OF_ITEM_PRICE]: '% of item price',
};

const GROUP_MATCH_LABELS: Record<string, string> = {
  [RuleEntity.COLLECTION]: 'Collection',
  [RuleEntity.PRODUCT_TYPE]: 'Product type',
  [RuleEntity.PRODUCT_TAG]: 'Tag',
  [RuleEntity.VENDOR]: 'Vendor',
};

const GROUP_OPERATOR_LABELS: Record<string, string> = {
  [RuleOperator.IS]: 'is',
  [RuleOperator.IS_NOT]: 'is not',
  [RuleOperator.CONTAINS]: 'contains',
  [RuleOperator.NOT_CONTAINS]: 'does not contain',
};

type GroupRule = Partial<Checkout.FeePerItemGroupRule>;

function describeGroupRule(rule: GroupRule, money: (value: unknown) => string): string {
  const subject = `${GROUP_MATCH_LABELS[rule.matchBy] ?? startCase(String(rule.matchBy))} ${
    GROUP_OPERATOR_LABELS[rule.operator] ?? String(rule.operator)
  } "${rule.value ?? ''}"`;
  const fee =
    rule.type === Checkout.GroupFeeType.PERCENT_OF_ITEM_PRICE
      ? `${Number(rule.amount ?? 0)}% of item price`
      : money(rule.amount ?? 0);
  // The evaluator returns before it reads step, stepUnit or rounding on a percentage rule, so
  // describing them here would promise grouping the shopper is never charged by.
  const isPercent = rule.type === Checkout.GroupFeeType.PERCENT_OF_ITEM_PRICE;
  const step = Number(rule.step ?? 1);
  const byWeight = !isPercent && rule.stepUnit === Checkout.FeeUnitType.POUNDS;
  const unit = byWeight ? 'lb' : 'item';
  const every = !isPercent && step > 1 ? `every ${step} ${unit}s` : `per ${unit}`;
  // same condition as the editor: a leftover exists above 1, or at any step by weight
  const hasPartialGroups = !isPercent && (step > 1 || byWeight) && rule.rounding;
  return [
    `${subject}: +${fee} ${every}`,
    rule.firstNFree ? `first ${rule.firstNFree} free` : '',
    hasPartialGroups ? `partial ${unit} ${rule.rounding === Checkout.GroupRounding.DOWN ? 'free' : 'charged'}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

function describeWeekdayFee(rule: WeekdayFee, money: (value: unknown) => string): string {
  const days = rule.numOfDays ?? 1;
  const dayLabels: Partial<Record<Checkout.ExtendedWeekdays, string>> = {
    [Checkout.ExtendedWeekdays.CUSTOM_DATE]: [formatDate(rule.customDate?.start), formatDate(rule.customDate?.end)]
      .filter(Boolean)
      .join(' – '),
    [Checkout.ExtendedWeekdays.DaysAfterHolidays]:
      `${days} days after ${rule.nationalHolidays?.country ?? ''} holidays`.replace('  ', ' '),
    [Checkout.ExtendedWeekdays.X_DAYS_FROM_TODAY]: `${days} days from today`,
  };
  const weekday = Number(rule.weekday) as Checkout.ExtendedWeekdays;
  const day =
    dayLabels[weekday] || Checkout.ExtendedWeekdaysToDisplayNameMap[weekday] || startCase(String(rule.weekday));
  return [
    `${day}: +${money(rule.amount ?? 0)}`,
    rule.shippingName && `name "${rule.shippingName}"`,
    rule.shippingNotes && `note "${rule.shippingNotes}"`,
  ]
    .filter(Boolean)
    .join(' · ');
}

function describeExcludedDay(day: ExcludedDay): string {
  const value = Array.isArray(day.value) ? day.value.join(', ') : String(day.value ?? '');
  return `${startCase(day.type ?? '')}${value ? `: ${value}` : ''}`;
}
